import { useState, useEffect } from 'react';
import { footerService } from '../../../services/api';
import { useLanguage } from '../../../context/LanguageContext';
import { toEnglishNumbers } from '../../../utils/languageUtils';
import FooterLoading from './FooterLoading';
import FooterError from './FooterError';
import DesktopFooter from './DesktopFooter';
import MobileFooter from './MobileFooter';

const Footer = () => {
    const { language } = useLanguage();
    const [activeTab, setActiveTab] = useState('call');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [apiData, setApiData] = useState({
        phones: [],
        branches: [],
        platforms: []
    });

    useEffect(() => {
        const fetchFooterData = async () => {
            setLoading(true);
            setError(null);
            
            try {
                const data = await footerService.getAllFooterData();
                setApiData({
                    phones: data.phones || [],
                    branches: data.branches || [],
                    platforms: data.platforms || []
                });
            } catch (err) {
                console.error('Footer API error:', err);
                setError(language === 'fa' ? 'خطا در دریافت اطلاعات' : 'Error fetching data');
            } finally {
                setLoading(false);
            }
        };

        fetchFooterData();
    }, [language]);

    const formatNumber = (number) => {
        if (!number) return '';
        return toEnglishNumbers(number);
    };

    const getFooterContent = () => {
        const localPhones = apiData.phones.filter(p => p.region === 'LOC');
        const intPhones = apiData.phones.filter(p => p.region === 'INT');
        const localBranches = apiData.branches.filter(b => b.region === 'LOC');
        const intBranches = apiData.branches.filter(b => b.region === 'INT');

        const getBranchAddress = (branch) => {
            return branch.address || '';
        };

        return {
            call: {
                phoneNumbers: localPhones.map(p => formatNumber(p.number)),
                international: intPhones.length > 0 ? {
                    label: language === 'fa' ? 'تلفن بین‌الملل' : 'International Phone',
                    number: formatNumber(intPhones[0].number)
                } : null
            },
            office: {
                addresses: [
                    ...localBranches.map(branch => ({
                        address: getBranchAddress(branch),
                        url: branch.url || null,  // URL eklendi
                        isInternational: false
                    })),
                    ...(intBranches.length > 0 ? [{
                        address: language === 'fa' ? 'دفتر بین‌الملل' : 'International Office',
                        url: null,
                        isInternational: true
                    }] : []),
                    ...intBranches.map(branch => ({
                        address: getBranchAddress(branch),
                        url: branch.url || null,  // URL eklendi
                        isInternational: false
                    }))
                ]
            },
            social: {
                items: apiData.platforms.map(platform => ({
                    icon: platform.icon,
                    title: platform.address || platform.url,
                    link: platform.url
                }))
            }
        };
    };

    const handleIconClick = (tab) => {
        setActiveTab(tab);
    };

    const content = getFooterContent();

    return (
        <div id='contact' className="w-full flex justify-center mx-auto mt-16 sm:mt-24 md:mt-32 max-w-[1136px] items-center flex-col pb-8 sm:pb-12 md:pb-14 px-3 sm:px-4 md:px-6">
            {loading ? (
                <>
                    <div className="w-full hidden md:block">
                        <FooterLoading />
                    </div>
                    <div className="w-full md:hidden">
                        <FooterLoading />
                    </div>
                </>
            ) : error ? (
                <>
                    <div className="w-full hidden md:block">
                        <FooterError message={error} />
                    </div>
                    <div className="w-full md:hidden">
                        <FooterError message={error} />
                    </div>
                </>
            ) : (
                <>
                    <DesktopFooter 
                        content={content} 
                        activeTab={activeTab} 
                        handleIconClick={handleIconClick}
                        language={language}
                    />
                    <MobileFooter 
                        content={content} 
                        activeTab={activeTab} 
                        handleIconClick={handleIconClick}
                        language={language}
                    />
                </>
            )}
        </div>
    );
};

export default Footer;